import { useState, useMemo, useRef, useCallback } from "react";
import { Link } from "wouter";
import AdminLayout from "@/components/layout-admin";
import {
  useAdminReservations,
  useCreateAdminReservation,
  useUpdateAdminReservation,
  useDeleteAdminReservation,
  useUpdateReservationStatus,
  useApartments,
  useAdminUsers,
  ReservationConflictError,
} from "@/hooks/use-resources";
import { useAuth } from "@/hooks/use-auth";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Download,
  Upload,
  FileSpreadsheet,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  RefreshCw,
  CheckSquare,
} from "lucide-react";
import type { Reservation, Apartment } from "@shared/routes";

type FilterStatus = "all" | "confirmed" | "checked_in" | "checked_out" | "cancelled";

const statusConfig: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  confirmed: { label: "Confirmed", variant: "default" },
  checked_in: { label: "Checked In", variant: "default" },
  checked_out: { label: "Checked Out", variant: "secondary" },
  cancelled: { label: "Cancelled", variant: "destructive" },
};

function getStatusClasses(status: string) {
  switch (status) {
    case "confirmed":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    case "checked_in":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    case "checked_out":
      return "";
    case "cancelled":
      return "";
    default:
      return "";
  }
}

function parseLocalDate(d: string | Date): Date {
  if (d instanceof Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  const iso = d.split("T")[0];
  const parts = iso.split("-");
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return new Date(d);
}

function formatDate(d: string | Date) {
  const local = parseLocalDate(d);
  return local.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateShort(d: Date) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function toDateString(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const emptyForm = {
  apartmentId: "",
  guestName: "",
  guestEmail: "",
  guestPhone: "",
  checkInDate: "",
  checkOutDate: "",
  status: "confirmed",
  notes: "",
  source: "manual",
  ownerUserId: "",
  totalPrice: "",
  paidAmount: "",
  paymentMethod: "",
  paymentStatus: "pending",
};

type OverlapConflict = {
  overlaps: any[];
  action: "create" | "update";
  formData: any;
} | null;

export default function AdminReservations() {
  const { data: reservations = [], isLoading } = useAdminReservations();
  const { data: apartments = [] } = useApartments();
  const { data: allUsers = [] } = useAdminUsers();
  const { user } = useAuth();
  const createReservation = useCreateAdminReservation();
  const updateReservation = useUpdateAdminReservation();
  const deleteReservation = useDeleteAdminReservation();
  const updateStatus = useUpdateReservationStatus();
  const { toast } = useToast();

  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [filterApartment, setFilterApartment] = useState<string>("all");
  const [filterSource, setFilterSource] = useState<string>("all");
  const [showCancelled, setShowCancelled] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [icalDeleteOpen, setIcalDeleteOpen] = useState(false);
  const [icalDeleting, setIcalDeleting] = useState(false);
  const [icalApartmentId, setIcalApartmentId] = useState<string>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [timelineOffset, setTimelineOffset] = useState(0);
  const [overlapConflict, setOverlapConflict] = useState<OverlapConflict>(null);
  const [forceSubmitting, setForceSubmitting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState<{ created: number; updated: number; skipped: number; total: number; results: any[] } | null>(null);
  const [importPreview, setImportPreview] = useState<{ totalRows: number; matched: number; unmatched: string[]; apartments: { id: number; name: string }[] } | null>(null);
  const [importNameMapping, setImportNameMapping] = useState<Record<string, number>>({});
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importStep, setImportStep] = useState<'upload' | 'preview' | 'mapping' | 'results'>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCSVPreview = async (file: File) => {
    setImportLoading(true);
    setImportPreview(null);
    setImportResults(null);
    setImportFile(file);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/admin/reservations/csv-preview', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Preview failed');
      }
      const data = await res.json();
      setImportPreview(data);
      if (data.unmatched.length > 0) {
        setImportStep('mapping');
        setImportNameMapping({});
      } else {
        setImportStep('preview');
      }
    } catch (err: any) {
      toast({ title: "Preview Error", description: err.message, variant: "destructive" });
    } finally {
      setImportLoading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.csv')) {
      handleCSVPreview(file);
    } else {
      toast({ title: "Invalid file", description: "Please upload a .csv file", variant: "destructive" });
    }
  };

  const executeImport = async (file: File, mapping: Record<string, number>) => {
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (Object.keys(mapping).length > 0) {
        formData.append('nameMapping', JSON.stringify(mapping));
      }
      const res = await fetch('/api/admin/reservations/csv-import', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Import failed');
      }
      const data = await res.json();
      setImportResults(data);
      setImportStep('results');
      if (data.created > 0 || data.updated > 0) {
        queryClient.invalidateQueries({ queryKey: ['/api/admin/reservations'] });
        const parts = [];
        if (data.created > 0) parts.push(`${data.created} created`);
        if (data.updated > 0) parts.push(`${data.updated} updated`);
        toast({ title: `Import complete: ${parts.join(', ')}` });
      }
    } catch (err: any) {
      toast({ title: "Import Error", description: err.message, variant: "destructive" });
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImportWithMapping = async () => {
    if (!importFile) return;
    await executeImport(importFile, importNameMapping);
  };

  const isSuperAdmin = (user as any)?.role === "super_admin";

  const typedReservations = reservations as Reservation[];
  const typedApartments = apartments as Apartment[];
  const typedUsers = allUsers as any[];
  const ownerUsers = useMemo(() => typedUsers.filter(u => u.role === "owner"), [typedUsers]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const timelineDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i + timelineOffset);
      days.push(d);
    }
    return days;
  }, [today, timelineOffset]);

  const getApartmentName = (apartmentId: number) => {
    const apt = typedApartments.find((a) => a.id === apartmentId);
    return apt?.name || `Apartment #${apartmentId}`;
  };

  const availableSources = useMemo(() => {
    const sources = new Set(typedReservations.map(r => (r as any).source).filter(Boolean));
    return Array.from(sources).sort();
  }, [typedReservations]);

  const filtered = useMemo(() => {
    return typedReservations.filter((r) => {
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (!showCancelled && filterStatus !== "cancelled" && r.status === "cancelled") return false;
      if (filterApartment !== "all" && r.apartmentId !== Number(filterApartment)) return false;
      if (filterSource !== "all" && (r as any).source !== filterSource) return false;
      return true;
    });
  }, [typedReservations, filterStatus, filterApartment, filterSource, showCancelled]);

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => prev.size === filtered.length ? new Set() : new Set(filtered.map(r => r.id)));
  }, [filtered]);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await apiRequest("POST", "/api/admin/reservations/bulk-delete", {
        reservationIds: Array.from(selectedIds),
      });
      if (!res.ok) throw new Error("Failed to delete");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/reservations"] });
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      toast({ title: `${selectedIds.size} reservations deleted` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setBulkDeleting(false);
    }
  };

  const icalReservations = useMemo(
    () => typedReservations.filter(r => (r as any).source === 'ical'),
    [typedReservations]
  );

  const icalCount = icalReservations.length;

  const icalCountByApartment = useMemo(() => {
    const map: Record<number, number> = {};
    icalReservations.forEach(r => {
      map[r.apartmentId] = (map[r.apartmentId] || 0) + 1;
    });
    return map;
  }, [icalReservations]);

  const icalSelectedCount = icalApartmentId === "all"
    ? icalCount
    : (icalCountByApartment[Number(icalApartmentId)] ?? 0);

  const handleDeleteIcal = async () => {
    setIcalDeleting(true);
    try {
      const body = icalApartmentId === "all" ? {} : { apartmentId: Number(icalApartmentId) };
      const res = await apiRequest("POST", "/api/admin/reservations/delete-ical", body);
      if (!res.ok) throw new Error("Failed to delete");
      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/admin/reservations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/cleaning-jobs"] });
      setIcalDeleteOpen(false);
      toast({ title: `Deleted ${data.deletedReservations} iCal reservations and ${data.deletedCleaningJobs} linked cleaning jobs` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setIcalDeleting(false);
    }
  };

  const sortedReservations = useMemo(() => {
    return [...filtered].sort(
      (a, b) => parseLocalDate(a.checkInDate).getTime() - parseLocalDate(b.checkInDate).getTime()
    );
  }, [filtered]);

  const todayCheckIns = useMemo(() => {
    return typedReservations.filter((r) => {
      const checkIn = parseLocalDate(r.checkInDate);
      return isSameDay(checkIn, today) && (r.status === "confirmed" || r.status === "checked_in");
    }).length;
  }, [typedReservations, today]);

  const activeCount = useMemo(() => {
    return typedReservations.filter(
      (r) => r.status === "confirmed" || r.status === "checked_in"
    ).length;
  }, [typedReservations]);

  const checkedOutCount = useMemo(() => {
    return typedReservations.filter((r) => r.status === "checked_out").length;
  }, [typedReservations]);

  const isUpcomingSoon = (r: Reservation) => {
    const checkIn = parseLocalDate(r.checkInDate);
    const diffMs = checkIn.getTime() - today.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 1 && r.status === "confirmed";
  };

  const handleApartmentChange = (aptId: string) => {
    setForm((p) => {
      const apt = typedApartments.find((a) => a.id === Number(aptId));
      return { ...p, apartmentId: aptId, ownerUserId: apt?.ownerUserId ? String(apt.ownerUserId) : "" };
    });
  };

  const buildCreateData = () => ({
    apartmentId: Number(form.apartmentId),
    guestName: form.guestName,
    guestEmail: form.guestEmail || undefined,
    guestPhone: form.guestPhone || undefined,
    checkInDate: form.checkInDate,
    checkOutDate: form.checkOutDate,
    status: form.status as any,
    notes: form.notes || undefined,
    source: form.source || undefined,
    ownerUserId: form.ownerUserId ? Number(form.ownerUserId) : undefined,
    totalPrice: form.totalPrice || undefined,
    paidAmount: form.paidAmount || undefined,
    paymentMethod: form.paymentMethod || undefined,
    paymentStatus: form.paymentStatus || undefined,
  });

  const handleCreate = async () => {
    if (!form.apartmentId || !form.guestName || !form.checkInDate || !form.checkOutDate) {
      toast({ title: "Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    try {
      await createReservation.mutateAsync(buildCreateData());
      setForm({ ...emptyForm });
      setCreateOpen(false);
      toast({ title: "Reservation created" });
    } catch (err: any) {
      if (err instanceof ReservationConflictError) {
        setOverlapConflict({ overlaps: err.overlaps, action: "create", formData: buildCreateData() });
      } else {
        toast({ title: "Error", description: err.message, variant: "destructive" });
      }
    }
  };

  const handleForceSubmit = async () => {
    if (!overlapConflict) return;
    setForceSubmitting(true);
    try {
      if (overlapConflict.action === "create") {
        await createReservation.mutateAsync({ ...overlapConflict.formData, _force: true });
        setForm({ ...emptyForm });
        setCreateOpen(false);
        toast({ title: "Reservation created (override)" });
      } else if (overlapConflict.action === "update" && editingId) {
        await updateReservation.mutateAsync({ id: editingId, data: overlapConflict.formData, force: true });
        setEditOpen(false);
        setEditingId(null);
        setForm({ ...emptyForm });
        toast({ title: "Reservation updated (override)" });
      }
      setOverlapConflict(null);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setForceSubmitting(false);
    }
  };

  const handleEdit = (r: Reservation) => {
    setEditingId(r.id);
    setForm({
      apartmentId: String(r.apartmentId),
      guestName: r.guestName,
      guestEmail: r.guestEmail || "",
      guestPhone: r.guestPhone || "",
      checkInDate: r.checkInDate ? toDateString(parseLocalDate(r.checkInDate)) : "",
      checkOutDate: r.checkOutDate ? toDateString(parseLocalDate(r.checkOutDate)) : "",
      status: r.status,
      notes: r.notes || "",
      source: r.source || "manual",
      ownerUserId: (r as any).ownerUserId ? String((r as any).ownerUserId) : "",
      totalPrice: (r as any).totalPrice || "",
      paidAmount: (r as any).paidAmount || "",
      paymentMethod: (r as any).paymentMethod || "",
      paymentStatus: (r as any).paymentStatus || "pending",
    });
    setEditOpen(true);
  };

  const buildUpdateData = () => ({
    guestName: form.guestName || undefined,
    guestEmail: form.guestEmail || undefined,
    guestPhone: form.guestPhone || undefined,
    checkInDate: form.checkInDate || undefined,
    checkOutDate: form.checkOutDate || undefined,
    status: form.status as any,
    notes: form.notes || undefined,
    source: form.source || undefined,
    ownerUserId: form.ownerUserId ? Number(form.ownerUserId) : undefined,
    totalPrice: form.totalPrice || undefined,
    paidAmount: form.paidAmount || undefined,
    paymentMethod: form.paymentMethod || undefined,
    paymentStatus: form.paymentStatus || undefined,
  });

  const handleUpdate = async () => {
    if (!editingId) return;
    try {
      await updateReservation.mutateAsync({
        id: editingId,
        data: buildUpdateData(),
      });
      setEditOpen(false);
      setEditingId(null);
      setForm({ ...emptyForm });
      toast({ title: "Reservation updated" });
    } catch (err: any) {
      if (err instanceof ReservationConflictError) {
        setOverlapConflict({ overlaps: err.overlaps, action: "update", formData: buildUpdateData() });
      } else {
        toast({ title: "Error", description: err.message, variant: "destructive" });
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteReservation.mutateAsync(id);
      toast({ title: "Reservation deleted" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleInlineStatusChange = async (id: number, newStatus: string) => {
    try {
      await updateStatus.mutateAsync({ id, status: newStatus });
      const label = statusConfig[newStatus]?.label || newStatus;
      toast({ title: `Status changed to ${label}` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const timelineApartments = useMemo(() => {
    const aptIds = new Set(typedReservations.map((r) => r.apartmentId));
    return typedApartments.filter((a) => aptIds.has(a.id));
  }, [typedReservations, typedApartments]);

  const getTimelineBlocks = (apartmentId: number) => {
    const startDate = timelineDays[0];
    const endDate = new Date(timelineDays[timelineDays.length - 1]);
    endDate.setDate(endDate.getDate() + 1);
    return typedReservations.filter((r) => {
      if (r.apartmentId !== apartmentId) return false;
      if (r.status === "cancelled") return false;
      const checkIn = parseLocalDate(r.checkInDate);
      const checkOut = parseLocalDate(r.checkOutDate);
      return checkOut > startDate && checkIn < endDate;
    }).map((r) => {
      const checkIn = parseLocalDate(r.checkInDate);
      const checkOut = parseLocalDate(r.checkOutDate);
      const visibleStart = checkIn < startDate ? startDate : checkIn;
      const visibleEnd = checkOut > endDate ? endDate : checkOut;
      const startCol = Math.max(0, Math.round((visibleStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
      const span = Math.max(1, Math.round((visibleEnd.getTime() - visibleStart.getTime()) / (1000 * 60 * 60 * 24)));
      return { ...r, startCol, span, checkIn, checkOut };
    });
  };

  const getTimelineColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-500/80 text-white dark:bg-green-600/80";
      case "checked_in":
        return "bg-blue-500/80 text-white dark:bg-blue-600/80";
      case "checked_out":
        return "bg-gray-400/70 text-white dark:bg-gray-500/60";
      case "cancelled":
        return "bg-red-500/80 text-white line-through dark:bg-red-600/80";
      default:
        return "bg-amber-400/80 text-white dark:bg-amber-500/70";
    }
  };

  const renderReservationForm = (onSubmit: () => void, isPending: boolean, submitLabel: string) => (
    <div className="space-y-4">
      <div>
        <Label>Apartment</Label>
        <SearchableSelect
          value={form.apartmentId}
          onValueChange={handleApartmentChange}
          placeholder="Select apartment"
          searchPlaceholder="Search apartment..."
          options={typedApartments.map((a) => ({ value: String(a.id), label: a.name }))}
          className="w-full"
          data-testid="select-apartment"
        />
      </div>
      <div>
        <Label>Guest Name</Label>
        <Input
          value={form.guestName}
          onChange={(e) => setForm((p) => ({ ...p, guestName: e.target.value }))}
          placeholder="John Smith"
          data-testid="input-guest-name"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Guest Email</Label>
          <Input
            type="email"
            value={form.guestEmail}
            onChange={(e) => setForm((p) => ({ ...p, guestEmail: e.target.value }))}
            placeholder="guest@example.com"
            data-testid="input-guest-email"
          />
        </div>
        <div>
          <Label>Guest Phone</Label>
          <Input
            value={form.guestPhone}
            onChange={(e) => setForm((p) => ({ ...p, guestPhone: e.target.value }))}
            placeholder="+1 555-0100"
            data-testid="input-guest-phone"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Check-in Date</Label>
          <Input
            type="date"
            value={form.checkInDate}
            onChange={(e) => setForm((p) => ({ ...p, checkInDate: e.target.value }))}
            data-testid="input-check-in"
          />
        </div>
        <div>
          <Label>Check-out Date</Label>
          <Input
            type="date"
            value={form.checkOutDate}
            onChange={(e) => setForm((p) => ({ ...p, checkOutDate: e.target.value }))}
            data-testid="input-check-out"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
            <SelectTrigger data-testid="select-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="checked_in">Checked In</SelectItem>
              <SelectItem value="checked_out">Checked Out</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Source</Label>
          <Select value={form.source} onValueChange={(v) => setForm((p) => ({ ...p, source: v }))}>
            <SelectTrigger data-testid="select-source">
              <SelectValue placeholder="Select source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="airbnb">Airbnb</SelectItem>
              <SelectItem value="booking">Booking.com</SelectItem>
              <SelectItem value="expedia">Expedia</SelectItem>
              <SelectItem value="vrbo">VRBO</SelectItem>
              <SelectItem value="direct">Direct</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {ownerUsers.length > 0 && (
        <div>
          <Label>Property Manager</Label>
          <Select value={form.ownerUserId || "none"} onValueChange={(v) => setForm((p) => ({ ...p, ownerUserId: v === "none" ? "" : v }))}>
            <SelectTrigger data-testid="select-owner">
              <SelectValue placeholder="Select property manager" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Property Manager</SelectItem>
              {ownerUsers.filter((u: any) => u.isActive !== false).map((u) => (
                <SelectItem key={u.id} value={String(u.id)}>
                  {u.fullName || u.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">Auto-filled from apartment. Change if needed.</p>
        </div>
      )}
      <div>
        <Label>Notes</Label>
        <Input
          value={form.notes}
          onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          placeholder="Special requests, late arrival, etc."
          data-testid="input-notes"
        />
      </div>
      <Separator />
      <div className="text-sm font-medium text-muted-foreground">Payment Tracking</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Total Price</Label>
          <Input
            type="number"
            step="0.01"
            value={form.totalPrice}
            onChange={(e) => setForm((p) => ({ ...p, totalPrice: e.target.value }))}
            placeholder="0.00"
            data-testid="input-total-price"
          />
        </div>
        <div>
          <Label>Paid Amount</Label>
          <Input
            type="number"
            step="0.01"
            value={form.paidAmount}
            onChange={(e) => setForm((p) => ({ ...p, paidAmount: e.target.value }))}
            placeholder="0.00"
            data-testid="input-paid-amount"
          />
        </div>
        <div>
          <Label>Payment Method</Label>
          <Select value={form.paymentMethod} onValueChange={(v) => setForm((p) => ({ ...p, paymentMethod: v }))}>
            <SelectTrigger data-testid="select-payment-method">
              <SelectValue placeholder="Select method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="credit_card">Credit Card</SelectItem>
              <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
              <SelectItem value="paypal">PayPal</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Payment Status</Label>
          <Select value={form.paymentStatus} onValueChange={(v) => setForm((p) => ({ ...p, paymentStatus: v }))}>
            <SelectTrigger data-testid="select-payment-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button className="w-full" onClick={onSubmit} disabled={isPending} data-testid="button-submit-reservation">
        {isPending ? "Saving..." : submitLabel}
      </Button>
    </div>
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold" data-testid="text-reservations-title">
              Reservations
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage guest reservations across all apartments
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a href="/api/admin/reservations/csv-export" download>
              <Button variant="outline" size="sm" data-testid="button-csv-export">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </a>
            <Dialog open={importOpen} onOpenChange={(open) => { setImportOpen(open); if (!open) { setImportResults(null); setImportPreview(null); setImportStep('upload'); setImportFile(null); setImportNameMapping({}); setIsDragging(false); } }}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="button-csv-import">
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Import Reservations from CSV</DialogTitle>
                  <DialogDescription>
                    Upload a CSV file from Reservation Key, Google Calendar, Airbnb, Booking.com, or any spreadsheet.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {importStep === 'upload' && (
                    <>
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center space-y-3 transition-colors ${isDragging ? "border-primary bg-primary/5" : "border-border"}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        data-testid="dropzone-csv"
                      >
                        <FileSpreadsheet className={`w-10 h-10 mx-auto ${isDragging ? "text-primary" : "text-muted-foreground"}`} />
                        <div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            className="hidden"
                            data-testid="input-csv-file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleCSVPreview(file);
                            }}
                          />
                          <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={importLoading}
                            data-testid="button-choose-csv"
                          >
                            {importLoading ? "Scanning..." : "Choose CSV File"}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {isDragging ? "Drop your CSV file here" : "Drag & drop a .csv file here, or click to browse"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Supports columns: Apartment, Guest Name, Guest Email, Guest Phone, Check-In Date, Check-Out Date, Status, Source, Notes, Paid Amount
                        </p>
                      </div>
                      <Separator />
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p className="font-medium">Supported formats:</p>
                        <p>Reservation Key / Google Calendar CSV exports are automatically detected. Standard CSV files with columns like Apartment, Guest Name, Check-In Date also work.</p>
                        <p className="font-medium mt-2">Apartment matching:</p>
                        <p>Names are fuzzy-matched. You can also set aliases for each apartment in its settings to handle different naming conventions.</p>
                      </div>
                    </>
                  )}

                  {importStep === 'preview' && importPreview && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                        <span>
                          Ready to import <strong>{importPreview.totalRows}</strong> {importPreview.totalRows === 1 ? "row" : "rows"}. All <strong>{importPreview.matched}</strong> apartment {importPreview.matched === 1 ? "name" : "names"} matched successfully.
                        </span>
                      </div>
                      {importFile && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground border rounded-lg p-2">
                          <FileSpreadsheet className="w-4 h-4 shrink-0" />
                          <span className="truncate">{importFile.name}</span>
                          <span className="shrink-0">({(importFile.size / 1024).toFixed(1)} KB)</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => { setImportStep('upload'); setImportPreview(null); setImportFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} data-testid="button-back-to-upload">
                          Back
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => { if (importFile) executeImport(importFile, {}); }}
                          disabled={importLoading}
                          data-testid="button-confirm-import"
                        >
                          {importLoading ? "Importing..." : `Import ${importPreview.totalRows} Rows`}
                        </Button>
                      </div>
                    </div>
                  )}

                  {importStep === 'mapping' && importPreview && (
                    <>
                      <div className="text-sm space-y-2">
                        <p>Found <strong>{importPreview.totalRows}</strong> rows. <span className="text-green-600">{importPreview.matched} matched</span>, <span className="text-amber-600">{importPreview.unmatched.length} unmatched</span>.</p>
                        <p className="text-muted-foreground text-xs">Map each unmatched name to one of your apartments, or leave unmapped to skip those rows.</p>
                      </div>
                      <div className="max-h-60 overflow-y-auto space-y-3">
                        {importPreview.unmatched.map((name) => (
                          <div key={name} className="border rounded-lg p-3 space-y-1.5">
                            <div className="text-sm font-medium flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                              <span className="truncate" data-testid={`text-unmatched-${name}`}>"{name}"</span>
                            </div>
                            <SearchableSelect
                              value={importNameMapping[name.toLowerCase().trim()]?.toString() || "skip"}
                              onValueChange={(val) => {
                                setImportNameMapping(prev => {
                                  const next = { ...prev };
                                  if (val === "skip") {
                                    delete next[name.toLowerCase().trim()];
                                  } else {
                                    next[name.toLowerCase().trim()] = Number(val);
                                  }
                                  return next;
                                });
                              }}
                              placeholder="Map to apartment..."
                              searchPlaceholder="Search apartment..."
                              options={[
                                { value: "skip", label: "Skip (don't import)" },
                                ...importPreview.apartments.map(a => ({ value: a.id.toString(), label: a.name })),
                              ]}
                              className="w-full text-xs"
                              data-testid={`select-mapping-${name}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => { setImportStep('upload'); setImportPreview(null); setImportFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} data-testid="button-mapping-back">
                          Back
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleImportWithMapping}
                          disabled={importLoading}
                          data-testid="button-mapping-import"
                        >
                          {importLoading ? "Importing..." : `Import ${importPreview.totalRows} Rows`}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Tip: To avoid this step next time, add aliases in each apartment's settings.
                      </p>
                    </>
                  )}

                  {importStep === 'results' && importResults && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 text-sm flex-wrap">
                        {importResults.created > 0 && (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                            {importResults.created} created
                          </span>
                        )}
                        {importResults.updated > 0 && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <RefreshCw className="w-4 h-4" />
                            {importResults.updated} updated
                          </span>
                        )}
                        {importResults.skipped > 0 && (
                          <span className="flex items-center gap-1 text-destructive">
                            <XCircle className="w-4 h-4" />
                            {importResults.skipped} skipped
                          </span>
                        )}
                        <span className="text-muted-foreground">
                          of {importResults.total} rows
                        </span>
                      </div>

                      {importResults.results.filter((r: any) => r.status !== 'created' && r.status !== 'updated').length > 0 && (
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {importResults.results
                            .filter((r: any) => r.status !== 'created' && r.status !== 'updated')
                            .map((r: any, i: number) => (
                              <div key={i} className="text-xs flex items-start gap-2 p-2 rounded bg-destructive/10">
                                <XCircle className="w-3.5 h-3.5 text-destructive shrink-0 mt-0.5" />
                                <span>
                                  Row {r.row}{r.guestName ? ` (${r.guestName})` : ''}: {r.error}
                                </span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <a href="/api/admin/reservations/ical" download>
              <Button variant="outline" size="sm" data-testid="button-ical-export">
                <Calendar className="w-4 h-4 mr-2" />
                iCal
              </Button>
            </a>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => setForm({ ...emptyForm })}
                  data-testid="button-create-reservation"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Reservation
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Reservation</DialogTitle>
                  <DialogDescription>
                    Add a new guest reservation to an apartment.
                  </DialogDescription>
                </DialogHeader>
                {renderReservationForm(handleCreate, createReservation.isPending, "Create Reservation")}
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <CalendarClock className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold" data-testid="text-today-checkins">
                {todayCheckIns}
              </p>
              <p className="text-xs text-muted-foreground">Check-ins Today</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <CalendarCheck className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold text-green-600" data-testid="text-active-reservations">
                {activeCount}
              </p>
              <p className="text-xs text-muted-foreground">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <CalendarX className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold text-muted-foreground" data-testid="text-checked-out-count">
                {checkedOutCount}
              </p>
              <p className="text-xs text-muted-foreground">Checked Out</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-base">Timeline</CardTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setTimelineOffset((o) => o - 7)}
                data-testid="button-timeline-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTimelineOffset(0)}
                data-testid="button-timeline-today"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setTimelineOffset((o) => o + 7)}
                data-testid="button-timeline-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {timelineApartments.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No reservations to display on the timeline.
              </p>
            ) : (
              <div className="min-w-[700px]">
                <div className="grid" style={{ gridTemplateColumns: `140px repeat(14, 1fr)` }}>
                  <div className="p-2 text-xs font-medium text-muted-foreground border-b">
                    Apartment
                  </div>
                  {timelineDays.map((day, i) => (
                    <div
                      key={i}
                      className={`p-1 text-center text-xs border-b ${
                        isSameDay(day, today) ? "bg-primary/10 font-bold" : ""
                      }`}
                    >
                      <div>{day.toLocaleDateString("en-US", { weekday: "short" })}</div>
                      <div>{day.getDate()}</div>
                    </div>
                  ))}

                  {timelineApartments.map((apt) => {
                    const blocks = getTimelineBlocks(apt.id);
                    return (
                      <div
                        key={`row-${apt.id}`}
                        className="contents"
                      >
                        <div className="p-2 text-sm font-medium truncate border-b flex items-center">
                          {apt.name}
                        </div>
                        <div className="col-span-14 border-b relative" style={{ gridColumn: 'span 14', minHeight: '40px' }}>
                          <div className="absolute inset-0 grid" style={{ gridTemplateColumns: 'repeat(14, 1fr)' }}>
                            {timelineDays.map((day, di) => (
                              <div key={di} className={`border-r last:border-r-0 ${isSameDay(day, today) ? "bg-primary/5" : ""}`} />
                            ))}
                          </div>
                          <div className="relative h-full py-0.5">
                            {blocks.map((block) => (
                              <div
                                key={block.id}
                                className={`absolute top-0.5 bottom-0.5 rounded-md px-1.5 flex items-center overflow-hidden cursor-pointer ${getTimelineColor(block.status)}`}
                                style={{
                                  left: `${(block.startCol / 14) * 100}%`,
                                  width: `${(block.span / 14) * 100}%`,
                                }}
                                title={`${block.guestName} (${formatDate(block.checkInDate)} - ${formatDate(block.checkOutDate)})`}
                                data-testid={`timeline-block-${block.id}`}
                                onClick={() => {
                                  const res = typedReservations.find(r => r.id === block.id);
                                  if (res) handleEdit(res);
                                }}
                              >
                                <span className="text-[11px] font-medium truncate">
                                  {block.guestName}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-green-500/80" />
                    <span>Confirmed</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-blue-500/80" />
                    <span>Checked In</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-gray-400/70" />
                    <span>Checked Out</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-red-500/80" />
                    <span>Cancelled</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as FilterStatus)}>
            <SelectTrigger className="w-full sm:w-[160px]" data-testid="select-filter-status">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="checked_in">Checked In</SelectItem>
              <SelectItem value="checked_out">Checked Out</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <SearchableSelect
            value={filterApartment}
            onValueChange={setFilterApartment}
            placeholder="All Apartments"
            searchPlaceholder="Search apartment..."
            options={[
              { value: "all", label: "All Apartments" },
              ...typedApartments.map((a) => ({ value: String(a.id), label: a.name })),
            ]}
            className="w-full sm:w-[180px] h-9 text-sm"
            data-testid="select-filter-apartment"
          />

          <SearchableSelect
            value={filterSource}
            onValueChange={setFilterSource}
            placeholder="All Sources"
            searchPlaceholder="Search source..."
            options={[
              { value: "all", label: "All Sources" },
              ...availableSources.map((s) => ({ value: s, label: s })),
            ]}
            className="w-full sm:w-[150px] h-9 text-sm"
            data-testid="select-filter-source"
          />

          <Button
            variant={showCancelled ? "secondary" : "outline"}
            size="sm"
            onClick={() => setShowCancelled(v => !v)}
            className="h-9 gap-1.5 whitespace-nowrap text-xs"
            data-testid="button-toggle-cancelled"
          >
            <div className={`w-2.5 h-2.5 rounded-full ${showCancelled ? "bg-gray-400" : "bg-gray-300"}`} />
            {showCancelled ? "Hide Cancelled" : "Show Cancelled"}
          </Button>

          {icalCount > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIcalDeleteOpen(true)}
              data-testid="button-delete-ical"
              className="gap-1 whitespace-nowrap"
            >
              <Trash2 className="w-4 h-4" />
              Delete All iCal ({icalCount})
            </Button>
          )}

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteOpen(true)}
                data-testid="button-bulk-delete"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Selected
              </Button>
            </div>
          )}
        </div>

        {sortedReservations.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={selectedIds.size === sortedReservations.length && sortedReservations.length > 0}
              onCheckedChange={toggleSelectAll}
              data-testid="checkbox-select-all"
            />
            <span className="text-muted-foreground">
              Select all {sortedReservations.length} visible reservations
            </span>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading reservations...</div>
        ) : sortedReservations.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No reservations found. Create one to get started.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedReservations.map((r) => {
              const config = statusConfig[r.status] || statusConfig.confirmed;
              const urgent = isUpcomingSoon(r);
              return (
                <Card key={r.id} data-testid={`card-reservation-${r.id}`} className={selectedIds.has(r.id) ? "ring-2 ring-primary" : ""}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex items-start gap-3">
                        <Checkbox
                          checked={selectedIds.has(r.id)}
                          onCheckedChange={() => toggleSelect(r.id)}
                          className="mt-0.5 flex-shrink-0"
                          data-testid={`checkbox-reservation-${r.id}`}
                        />
                        <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium" data-testid={`text-guest-name-${r.id}`}>
                            {r.guestName}
                          </span>
                          {urgent && (
                            <Badge variant="destructive" className="gap-1" data-testid={`badge-urgent-${r.id}`}>
                              <AlertTriangle className="w-3 h-3" />
                              Check-in soon
                            </Badge>
                          )}
                        </div>
                        <Link href={`/admin/apartments/${r.apartmentId}`} className="text-sm text-muted-foreground hover:underline" data-testid={`link-reservation-apartment-${r.id}`}>
                          {getApartmentName(r.apartmentId)}
                        </Link>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Select
                          value={r.status}
                          onValueChange={(val) => handleInlineStatusChange(r.id, val)}
                        >
                          <SelectTrigger
                            className={`w-[140px] text-xs font-medium ${getStatusClasses(r.status)}`}
                            data-testid={`select-status-${r.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="confirmed" data-testid="option-confirmed">Confirmed</SelectItem>
                            <SelectItem value="checked_in" data-testid="option-checked-in">Checked In</SelectItem>
                            <SelectItem value="checked_out" data-testid="option-checked-out">Checked Out</SelectItem>
                            <SelectItem value="cancelled" data-testid="option-cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      <span data-testid={`text-dates-${r.id}`}>
                        {formatDate(r.checkInDate)} — {formatDate(r.checkOutDate)}
                      </span>
                      {r.source && r.source !== "manual" && (
                        <Badge variant="outline" className="text-xs">
                          {r.source}
                        </Badge>
                      )}
                      {(r as any).totalPrice && (
                        <span className="font-medium text-foreground" data-testid={`text-price-${r.id}`}>
                          ${Number((r as any).totalPrice).toFixed(2)}
                        </span>
                      )}
                      {(r as any).paymentStatus && (r as any).paymentStatus !== "pending" && (
                        <Badge
                          variant={(r as any).paymentStatus === "paid" ? "default" : "outline"}
                          className="text-xs"
                          data-testid={`badge-payment-${r.id}`}
                        >
                          {(r as any).paymentStatus}
                        </Badge>
                      )}
                    </div>

                    {r.notes && (
                      <p className="text-sm text-muted-foreground">{r.notes}</p>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(r)}
                        data-testid={`button-edit-reservation-${r.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => handleDelete(r.id)}
                        data-testid={`button-delete-reservation-${r.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Reservation</DialogTitle>
              <DialogDescription>
                Update the reservation details below.
              </DialogDescription>
            </DialogHeader>
            {renderReservationForm(handleUpdate, updateReservation.isPending, "Save Changes")}
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!overlapConflict} onOpenChange={(open) => { if (!open) setOverlapConflict(null); }}>
          <AlertDialogContent data-testid="dialog-overlap-conflict">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Reservation Conflict
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <p className="mb-3">
                    This reservation overlaps with existing bookings:
                  </p>
                  <div className="space-y-2">
                    {overlapConflict?.overlaps.map((o: any, i: number) => (
                      <div key={i} className="rounded-md border p-3 text-sm" data-testid={`overlap-item-${i}`}>
                        <p className="font-medium">{o.guestName}</p>
                        <p className="text-muted-foreground">
                          {formatDate(o.checkInDate)} — {formatDate(o.checkOutDate)}
                        </p>
                      </div>
                    ))}
                  </div>
                  {!isSuperAdmin && (
                    <p className="mt-3 text-sm text-destructive" data-testid="text-conflict-no-override">
                      Cannot create reservation — a scheduling conflict exists. Contact a super admin to override.
                    </p>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-conflict-cancel">Cancel</AlertDialogCancel>
              {isSuperAdmin && (
                <AlertDialogAction
                  onClick={handleForceSubmit}
                  disabled={forceSubmitting}
                  data-testid="button-force-create"
                >
                  {forceSubmitting ? "Saving..." : `Force ${overlapConflict?.action === "create" ? "Create" : "Update"}`}
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      <Dialog open={icalDeleteOpen} onOpenChange={open => { if (!icalDeleting) { setIcalDeleteOpen(open); if (!open) setIcalApartmentId("all"); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete iCal Reservations</DialogTitle>
            <DialogDescription>
              Choose which unit to clear, or delete from all units at once. This also removes the directly linked cleaning jobs and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Unit</Label>
              <Select value={icalApartmentId} onValueChange={setIcalApartmentId} disabled={icalDeleting}>
                <SelectTrigger data-testid="select-ical-apartment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All units ({icalCount} reservations)</SelectItem>
                  {Object.entries(icalCountByApartment).map(([aptId, count]) => {
                    const apt = typedApartments.find(a => a.id === Number(aptId));
                    return (
                      <SelectItem key={aptId} value={aptId}>
                        {apt?.name ?? `Unit #${aptId}`} ({count} reservations)
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            {icalSelectedCount > 0 ? (
              <p className="text-sm text-muted-foreground rounded-md bg-destructive/10 border border-destructive/20 p-3">
                <strong className="text-destructive">{icalSelectedCount} reservations</strong> and their cleaning jobs will be permanently deleted.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">No iCal reservations found for this unit.</p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setIcalDeleteOpen(false); setIcalApartmentId("all"); }} disabled={icalDeleting} data-testid="button-ical-delete-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteIcal}
              disabled={icalDeleting || icalSelectedCount === 0}
              data-testid="button-ical-delete-confirm"
            >
              {icalDeleting ? "Deleting..." : `Delete ${icalSelectedCount} reservations`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} reservation{selectedIds.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all selected reservations and their related data (financial records, cleaning jobs, access codes, etc.). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting} data-testid="button-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-bulk-delete-confirm"
            >
              {bulkDeleting ? "Deleting..." : `Delete ${selectedIds.size} reservation${selectedIds.size !== 1 ? "s" : ""}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      </div>
    </AdminLayout>
  );
}
